import { service } from '@ember/service';
import Component from '@glimmer/component';
import LineClamp from 'garaje/components/line-clamp';
import removedString from 'garaje/helpers/removed-string';
import type Entry from 'garaje/models/entry';
import { DataCategory } from 'garaje/models/field-setting';
import type Invite from 'garaje/models/invite';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';

interface PrivateNotesColumnSignature {
  Args: {
    entry: Entry | Invite;
  };
  Element: HTMLDivElement;
}

/**
 * Private-notes cell for the visitor-log / invites grids. Private notes is a
 * sensitive data *category*, not a sign-in field, so its annotation + reveal
 * key is the category label (DataCategory.PRIVATE_NOTES) — NOT the "Private
 * Notes" column header name, which would silently miss the annotation and leak
 * the value. See VIS-17460.
 */
export default class PrivateNotesColumn extends Component<PrivateNotesColumnSignature> {
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;

  get sensitiveState(): SensitiveState | undefined {
    return this.args.entry.sensitiveFields?.[DataCategory.PRIVATE_NOTES];
  }

  get isSensitiveMasked(): boolean {
    return isSensitiveFieldMasked(
      this.sensitiveState,
      this.sensitiveFieldsVisibility.isRevealed(DataCategory.PRIVATE_NOTES),
    );
  }

  get value(): string {
    const note = this.args.entry.privateNotes;
    if (!note) return '';
    return this.isSensitiveMasked ? 'Hidden' : note;
  }

  <template>
    <div
      data-private
      data-test-private-notes-column
      class="table-cell border-b align-middle p-2 text-carbon-50 max-w-[18rem]"
      ...attributes
    >
      {{#if this.value}}
        <div class="h-12 flex items-center overflow-y-hidden">
          <LineClamp
            class="m-0 italic break-words"
            @useNative={{true}}
            @text={{removedString this.value}}
            @lines={{2}}
            @lineHeight={{1.4}}
            {{italicizeIfRemoved this.value}}
            data-test-private-notes
          />
        </div>
      {{/if}}
    </div>
  </template>
}
